<template>
  <div class="next-race-view">
    <router-link class="back-link" :to="{ name: 'results' }">
      &larr; {{ $t("next_race_back") }}
    </router-link>

    <div v-if="loading" class="loading">{{ $t("loading_results") }}...</div>

    <template v-else>
      <header v-if="!latest" class="motivation">
        <div class="motivation-label">{{ $t("road_to_glory") }}</div>
        <h1 class="motivation-title">{{ $t("next_race_first_headline") }}</h1>
        <p class="motivation-text">{{ $t("next_race_no_results") }}</p>
      </header>
      <header v-else class="motivation">
        <div class="motivation-label">{{ $t("road_to_glory") }}</div>
        <h1 class="motivation-title">{{ $t("next_race_headline") }}</h1>
        <p class="motivation-text">
          <template v-if="beat !== null">
            {{ $t("next_race_beat", { race: latest.event_name, percent: beat, time: latest.completion_time }) }}
          </template>
          {{ $t("next_race_push") }}
        </p>
      </header>

      <next-race-card
        v-if="nextRace"
        :result-id="latest && latest.result_id"
        :recommendation="nextRace"
      />
      <div v-else class="empty panel">
        <p v-if="latest">{{ $t("next_race_empty") }}</p>
        <find-race-button :text="'register'" />
      </div>

      <section
        v-if="timeline.steps.length"
        class="timeline panel"
        aria-labelledby="timeline-title"
      >
        <div class="timeline-head">
          <h2 id="timeline-title" class="timeline-title">
            {{ $t("road_to_glory") }}
          </h2>
          <div v-if="timeline.months" class="timeline-sub">
            <b>{{ timeline.months }} {{ $t("road_months") }}</b>
            {{ $t("road_to_ultra") }}
          </div>
        </div>

        <ol class="steps">
          <li
            v-for="(step, i) in timeline.steps"
            :key="i"
            :class="['step', `is-${step.state}`]"
          >
            <span class="step-dot" aria-hidden="true">
              <template v-if="step.state === 'done'">&#10003;</template>
            </span>

            <router-link
              v-if="step.state === 'done'"
              class="step-card"
              :to="{
                name: 'resultPage',
                params: { eventId: step.result.event_id, id: step.result.result_id },
              }"
            >
              <span class="step-tag">{{ $t("road_completed") }}</span>
              <span class="step-type">{{ step.type }}</span>
              <span class="step-name">{{ step.result.event_name }}</span>
              <span class="step-meta">
                {{ formatDate(step.result.event_date) }}
                <template v-if="step.result.completion_time">
                  &middot; {{ step.result.completion_time }}
                </template>
              </span>
            </router-link>

            <div v-else-if="step.state === 'next'" class="step-card">
              <span class="step-tag">{{ $t("road_up_next") }}</span>
              <span class="step-type">{{ step.type }}</span>
              <span class="step-name">{{ nextRace.event.name }}</span>
              <span class="step-meta">
                {{ formatDate(nextRace.event.start_date) }} &middot;
                {{ $t("next_race_in_weeks", { weeks: nextRace.weeks_out }) }}
              </span>
            </div>

            <div v-else class="step-card">
              <span class="step-tag">
                {{ step.state === "goal" ? $t("road_goal") : $t("road_then") }}
              </span>
              <span class="step-type">{{ step.type }}</span>
            </div>
          </li>
        </ol>
      </section>

      <section v-if="latest" class="panel">
        <lifetime-stats :stats="userStats" />
      </section>
    </template>
  </div>
</template>
<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { raceTimeline, latestResult, beatPercent, loadRec, saveRec } from "@/common/nextRace.mjs";
import FindRaceButton from "@/components/FindRaceButton.vue";
import LifetimeStats from "@/components/LifetimeStats.vue";
import NextRaceCard from "./components/NextRaceCard.vue";

export default {
  components: { NextRaceCard, FindRaceButton, LifetimeStats },
  data: () => ({ nextRace: null, loading: true }),
  computed: {
    ...mapGetters(["userResults", "userStats"]),
    latest() {
      return latestResult(this.userResults);
    },
    beat() {
      return beatPercent(this.latest);
    },
    timeline() {
      return raceTimeline(this.userResults, this.nextRace);
    },
  },
  methods: {
    ...mapActions(["getUserResults", "getNextRace"]),
    formatDate: (d) => moment(d, "YYYY-MM-DD").format("MMM DD, YYYY"),
  },
  async mounted() {
    try {
      if (!this.userResults.length) await this.getUserResults();
      this.nextRace = await this.getNextRace(this.latest?.result_id);
      // A card click is the stronger signal, so a page view never overwrites it.
      if (this.nextRace && !loadRec(localStorage).rec_source)
        saveRec(localStorage, { rec_source: "next_race_page", rec_id: `${this.nextRace.event.id}` });
    } finally {
      this.loading = false;
    }
  },
};
</script>
<style lang="scss" scoped>
.next-race-view {
  max-width: 1000px;
  padding-bottom: 120px;
  color: #ffffff;
  .back-link {
    display: inline-block;
    margin-bottom: 24px;
    color: #9d9d9d;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    text-decoration: none;
    &:hover,
    &:focus-visible {
      color: #ffffff;
    }
  }
  .motivation {
    margin-bottom: 28px;
  }
  .motivation-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #cf1019;
  }
  .motivation-title {
    margin: 6px 0 10px;
    font-family: Druk;
    font-size: 64px;
    font-weight: 900;
    line-height: 64px;
    text-transform: uppercase;
    @media screen and (max-width: $mobile) {
      font-size: 42px;
      line-height: 44px;
    }
  }
  .motivation-text {
    max-width: 600px;
    font-size: 16px;
    line-height: 24px;
    color: #d0d0d0;
  }
  .panel {
    margin-top: 20px;
    padding: 24px 28px;
    background: #121212;
    @media screen and (max-width: $mobile) {
      padding: 20px;
    }
  }
  .timeline-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
  }
  .timeline-title {
    margin: 0;
    font-family: Druk;
    font-size: 30px;
    font-weight: 900;
    text-transform: uppercase;
  }
  .timeline-sub {
    font-size: 12px;
    text-transform: uppercase;
  }
  .steps {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .step {
    position: relative;
    display: grid;
    grid-template-columns: 28px 1fr;
    gap: 18px;
    padding-bottom: 18px;
    &:not(:last-child)::before {
      content: "";
      position: absolute;
      top: 24px;
      bottom: 0;
      left: 13px;
      border-left: 2px dotted #555555;
    }
    &.is-done:not(:last-child)::before {
      border-left: 2px solid #cf1019;
    }
  }
  .step-dot {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border: 2px solid #555555;
    border-radius: 50%;
    background: #121212;
    font-size: 13px;
  }
  .step-card {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 14px 18px;
    background: #1d1d1d;
    color: #ffffff;
    text-decoration: none;
  }
  a.step-card:hover,
  a.step-card:focus-visible {
    background: #262626;
  }
  .step-tag {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #9d9d9d;
  }
  .step-type {
    font-family: Druk;
    font-size: 22px;
    font-weight: 900;
    text-transform: uppercase;
  }
  .step-name {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
  }
  .step-meta {
    font-size: 12px;
    color: #bdbdbd;
  }
  .is-done .step-dot {
    border-color: #cf1019;
    background: #cf1019;
  }
  .is-next {
    .step-dot {
      border-color: #cf1019;
      animation: pulse 1.6s infinite;
    }
    .step-card {
      background: #cf1019;
    }
    .step-tag,
    .step-meta {
      color: #ffffff;
    }
  }
  .is-goal {
    .step-dot {
      border-color: #ffffff;
    }
    .step-card {
      background: #ffffff;
      color: #cf1019;
    }
    .step-tag {
      color: #cf1019;
    }
  }
  .panel ::v-deep .lifetime-stats {
    margin-bottom: 0;
  }
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(207, 16, 25, 0.7);
  }
  100% {
    box-shadow: 0 0 0 10px rgba(207, 16, 25, 0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .next-race-view .is-next .step-dot {
    animation: none;
  }
}
</style>
